import payload from '@app/payload';
import Config from '@config';
import { detectLocale, getLocaleInfo } from '@i18n';
import H from '@utils/helpers';
import {
  getStripeCatalog,
  isStripeProvider,
  normalizeCountry,
  PAYMENT_PROVIDERS,
  stripeAvailable,
} from '@utils/provider';

import type { CheckoutValues } from '../types';

// `?test=true` fills the form with the team's test buyer and Stripe's 4242
// test card. Explicit prefill params still win, and the card values only
// reach the Asaas form — Stripe Elements iframes cannot be prefilled.
const TEST_PREFILL = {
  name: 'Ginastee Test User',
  email: 'tests@ginastee.com',
  phone: '+5511919829965',
  card: {
    number: '4242424242424242',
    expMonth: '12',
    expYear: '2036',
    cvv: '123',
  },
};

export function createInitialCheckoutValues(): CheckoutValues {
  const prefill = getCheckoutPrefill();
  const test = isTestCheckout() ? TEST_PREFILL : null;
  const name = prefill.name || test?.name || '';

  return {
    plan: prefill.plan ? parseInt(prefill.plan, 10) : payload.plans[0].id,
    name,
    email: prefill.email || test?.email || '',
    phone: prefill.phone || test?.phone || '',
    cpf: prefill.cpf || '',
    country: prefill.country || '',
    installments: prefill.installments ? parseInt(prefill.installments, 10) : null,
    method: prefill.method || 'credit',
    card: {
      number: test?.card.number || '',
      holderName: name,
      expMonth: test?.card.expMonth || '',
      expYear: test?.card.expYear || '',
      cvv: test?.card.cvv || '',
    },
    address: {},
  };
}

export function isTestCheckout() {
  return String(H.getUrlParam('test') || '').toLowerCase() === 'true';
}

export function getInitialBuyerCountry() {
  const fromUrl = normalizeCountry(H.getUrlParam('country') || H.getUrlParam('buyerCountry'));

  if (fromUrl) {
    return fromUrl;
  }

  const geoCountry = normalizeCountry(payload.buyerCountry || payload.buyer_country || payload.country);

  if (geoCountry) {
    return geoCountry;
  }

  // Legacy links prefilled the currency instead of the country. Otherwise,
  // derive the initial country from the browser's supported locale.
  if (String(H.getUrlParam('currency') || '').toUpperCase() === 'USD') {
    return 'US';
  }

  return getLocaleInfo(undefined, detectLocale()).countryCode;
}

// Older product payloads carry no `payments` block — absence must read as
// enabled so an API deploy lag can never block every checkout at once.
export function getPaymentsAvailability() {
  const payments = payload.payments || {};

  return {
    national: payments.national ? !!payments.national.enabled : true,
    international: payments.international ? !!payments.international.enabled : true,
  };
}

export function resolveCheckoutPricing(plan: any, buyerCountry: string) {
  const stripeCatalog = getStripeCatalog(plan);
  const stripePublishableKey = payload.stripePublishableKey || Config.stripePublishableKey;
  const stripeAmount = stripeCatalog?.amount || plan.usdPrice || plan.usd_price || 0;
  const usdAvailable = stripeAvailable(plan) && !!stripePublishableKey && stripeAmount > 0;
  const normalizedCountry = normalizeCountry(buyerCountry);
  const international = !!normalizedCountry && normalizedCountry !== 'BR';
  const availability = getPaymentsAvailability();
  // International sales run through Stripe only — an unapproved wallet or a
  // missing USD catalog blocks the checkout instead of silently falling back
  // to BRL/Asaas; national sales block while the Asaas wallet isn't approved.
  const paymentsBlocked = international
    ? !(availability.international && usdAvailable)
    : !availability.national;
  const currency = usdAvailable && international ? 'USD' : 'BRL';
  const provider = currency === 'USD' ? PAYMENT_PROVIDERS.STRIPE : PAYMENT_PROVIDERS.ASAAS;

  return {
    stripeCatalog,
    stripePublishableKey,
    usdAvailable,
    currency,
    provider,
    isStripe: isStripeProvider(provider),
    paymentsBlocked,
  };
}

export function ensurePlanInstallments(values: CheckoutValues, plan: any) {
  if (values.installments) {
    return;
  }

  values.installments = plan.installments > 1 ? plan.installments : 1;
}

function getCheckoutPrefill() {
  return {
    plan: H.getUrlParam('plan'),
    name: H.getUrlParam('name'),
    email: H.getUrlParam('email'),
    phone: H.getUrlParam('phone'),
    cpf: H.getUrlParam('cpf'),
    country: H.getUrlParam('country') || H.getUrlParam('buyerCountry'),
    installments: H.getUrlParam('installments'),
    method: H.getUrlParam('method'),
  };
}
